/**
 * File: renderPublicRoutes.js
 * Origin: isotect
 * Description: Functions that render public routes.
 *
 * Version: v1.0.1
 * Created: 2026-06-21
 * Last Updated: 2026-06-23
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { Route } from "react-router-dom";
import { useState } from "react";

// Local project files
import { PublicLayout } from "../components/layout/RouteLayouts";
import { routes } from "../constants/routes";
import * as schemas from "../schemas/schemas";
import ForgotPasswordPage from "../pages/ForgotPasswordPage";
import HomePage from "../pages/HomePage";
import ProductDetailPage from "../pages/ProductDetailPage";
import ProductsPage from "../pages/ProductsPage";
import ResetPasswordPage from "../pages/ResetPasswordPage";
import SignInPage from "../pages/SignInPage";
import SignUpPage from "../pages/SignUpPage";
import VerificationCodePage from "../pages/VerificationCodePage";

/**
 * Creates the route configuration for all public routes.
 *
 * @param {(isUserAuth: boolean) => void} setIsUserAuth - Updates the user's authentication state
 *
 * @returns {React.ReactElement} A Route element containing all public routes
 */
function renderPublicRoutes(setIsUserAuth) {
    return (
        <>
            {renderAuthRoutes(setIsUserAuth)}
            {renderHomeRoutes()}
            {renderProductRoutes()}
            {renderServiceRoutes()}
        </>
    );
}

/**
 * Creates the route configuration for public authentication routes.
 *
 * @param {(isUserAuth: boolean) => void} setIsUserAuth - Updates the user's authentication state
 *
 * @returns {React.ReactElement} A Route element containing authentication-related routes
 */
function renderAuthRoutes(setIsUserAuth) {
    const [otpEmail, setOtpEmail] = useState("");

    return (
        <Route path={routes.public.auth.root} element={<PublicLayout />}>
            <Route
                path={routes.public.auth.signIn.rel}
                element={
                    <SignInPage
                        schema={schemas.signInPage}
                        setIsUserAuth={setIsUserAuth}
                    />
                }
            />
            <Route
                path={routes.public.auth.signUp.rel}
                element={
                    <SignUpPage
                        schema={schemas.signUpPage}
                        setIsUserAuth={setIsUserAuth}
                    />
                }
            />
            <Route
                path={routes.public.auth.recovery.rel}
                element={
                    <ForgotPasswordPage
                        schema={schemas.forgotPasswordPage}
                        otpEmail={otpEmail}
                        setOtpEmail={setOtpEmail}
                    />
                }
            />
            <Route
                path={routes.public.auth.reset.rel}
                element={
                    <ResetPasswordPage
                        schema={schemas.resetPasswordPage}
                        otpEmail={otpEmail}
                    />
                }
            />
            <Route
                path={routes.public.auth.verify.rel}
                element={
                    <VerificationCodePage
                        schema={schemas.verificationCodePage}
                        otpEmail={otpEmail}
                    />
                }
            />
        </Route>
    );
}

/**
 * Creates the route configuration for public home routes.
 *
 * @returns {React.ReactElement} A Route element containing home routes
 */
function renderHomeRoutes() {
    return (
        <>
            <Route path={routes.public.home.root} element={<PublicLayout />}>
                <Route index element={<HomePage schema={schemas.homePage} />} />
            </Route>
        </>
    );
}

/**
 * Creates the route configuration for public product routes.
 *
 * @returns {React.ReactElement} A Route element containing product-related routes.
 */
function renderProductRoutes() {
    return (
        <>
            <Route
                path={routes.public.products.root}
                element={<PublicLayout />}
            >
                <Route
                    index
                    element={<ProductsPage schema={schemas.productsPage} />}
                />
                <Route
                    path={routes.public.products.flowkit.rel}
                    element={<ProductDetailPage schema={schemas.flowkitPage} />}
                />
                <Route
                    path={routes.public.products.isocheck.rel}
                    element={
                        <ProductDetailPage schema={schemas.isocheckPage} />
                    }
                />
                <Route
                    path={routes.public.products.nameplate.rel}
                    element={
                        <ProductDetailPage schema={schemas.nameplatePage} />
                    }
                />
                <Route
                    path={routes.public.products.tecthub.rel}
                    element={<ProductDetailPage schema={schemas.tecthubPage} />}
                />
            </Route>
        </>
    );
}

/**
 * Creates the route configuration for public service routes.
 *
 * @returns {React.ReactElement} A Route element containing service-related routes.
 */
function renderServiceRoutes() {
    return (
        <>
            <Route
                path={routes.public.services.root}
                element={<PublicLayout />}
            >
                <Route
                    index
                    element={<ProductsPage schema={schemas.servicesPage} />}
                />
                <Route
                    path={routes.public.services.drafting.rel}
                    element={
                        <ProductDetailPage schema={schemas.draftingPage} />
                    }
                />
                <Route
                    path={routes.public.services.engineering.rel}
                    element={
                        <ProductDetailPage schema={schemas.engineeringPage} />
                    }
                />
                <Route
                    path={routes.public.services.qaqc.rel}
                    element={<ProductDetailPage schema={schemas.qaqcPage} />}
                />
                <Route
                    path={routes.public.services.surveying.rel}
                    element={
                        <ProductDetailPage schema={schemas.surveyingPage} />
                    }
                />
            </Route>
        </>
    );
}

export { renderPublicRoutes };
